import { useState, useEffect, useRef } from 'react';
import { Card, Input, Button, Table, Switch, message, Space, Tag, Modal, Form } from 'antd';
import { LockOutlined, KeyOutlined, CheckCircleOutlined, ApiOutlined, LogoutOutlined, PlusOutlined } from '@ant-design/icons';

const OperatorAuth = () => {
  const [password, setPassword] = useState('');
  const [verified, setVerified] = useState(() => sessionStorage.getItem('op_verified') === '1');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState({});
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm] = Form.useForm();
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdForm] = Form.useForm();

  useEffect(() => { if (verified) fetchProviders(); }, [verified]);

  const verifyPassword = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/operator/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }).then(r => r.json());
      if (res.code === 0) { sessionStorage.setItem('op_verified', '1'); setVerified(true); fetchProviders(); }
      else { message.error('密码错误'); }
    } catch (e) { message.error('验证失败'); }
    setLoading(false);
  };

  const fetchProviders = async () => {
    try {
      const res = await fetch('/api/operator/providers', { method: 'POST' }).then(r => r.json());
      if (res.code === 0) setProviders(res.data?.providers || []);
    } catch (e) { message.error('加载失败'); }
  };

  const toggleProvider = async (providerId, checked) => {
    setSaving(prev => ({ ...prev, [providerId]: true }));
    const newProviders = providers.map(p =>
      p.id === providerId ? { ...p, authorized: checked } : p
    );
    setProviders(newProviders);
    try {
      const ids = newProviders.filter(p => p.authorized).map(p => p.id);
      await fetch('/api/operator/update-providers', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_ids: ids }),
      }).then(r => r.json());
    } catch (e) { message.error('更新失败'); fetchProviders(); }
    setSaving(prev => ({ ...prev, [providerId]: false }));
  };

  const handleChangePassword = async (values) => {
    if (values.new !== values.confirm) {
      message.error('两次输入的密码不一致');
      return;
    }
    setPwdLoading(true);
    try {
      const res = await fetch('/api/operator/change-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: values.old, new_password: values.new }),
      }).then(r => r.json());
      if (res.code === 0) { message.success('密码已修改'); setPwdModalVisible(false); pwdForm.resetFields(); }
      else { message.error(res.message || '修改失败'); }
    } catch (e) { message.error('修改失败'); }
    setPwdLoading(false);
  };

  const handleAddProvider = async (values) => {
    setAddLoading(true);
    try {
      const res = await fetch('/api/operator/add-provider', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      }).then(r => r.json());
      if (res.code === 0) { message.success('供应商已添加'); setAddModalVisible(false); addForm.resetFields(); fetchProviders(); }
      else { message.error(res.message || '添加失败'); }
    } catch (e) { message.error('添加失败'); }
    setAddLoading(false);
  };

  if (!verified) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
        <Card style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <LockOutlined style={{ fontSize: 48, color: '#1677ff' }} />
            <h2>系统管理</h2>
          </div>
          <Input.Password
            size="large" prefix={<KeyOutlined />}
            placeholder="请输入管理密码"
            value={password} onChange={e => setPassword(e.target.value)}
            onPressEnter={verifyPassword} style={{ marginBottom: 16 }}
          />
          <Button type="primary" size="large" block loading={loading} onClick={verifyPassword}>验证</Button>
        </Card>
      </div>
    );
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '供应商名称', dataIndex: 'display', width: 160 },
    { title: '标识', dataIndex: 'name', width: 100 },
    { title: 'API地址', dataIndex: 'api_base', width: 280, ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 80, render: s => <Tag color={s === 'active' ? 'green' : 'default'}>{s === 'active' ? '启用' : '停用'}</Tag> },
    { title: '授权接入', width: 100, render: (_, r) => (
      <Switch checked={r.authorized} onChange={v => toggleProvider(r.id, v)} loading={saving[r.id]} />
    )},
  ];

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}><ApiOutlined style={{ color: '#1677ff', marginRight: 8 }} />供应商接入管理</h2>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>添加供应商</Button>
          <Button icon={<KeyOutlined />} onClick={() => setPwdModalVisible(true)}>修改密码</Button>
          <Button onClick={fetchProviders}>刷新</Button>
          <Button icon={<LogoutOutlined />} danger onClick={() => { sessionStorage.removeItem('op_verified'); setVerified(false); setPassword(''); }}>退出</Button>
        </Space>
      </div>
      <Card>
        <Table dataSource={providers} columns={columns} rowKey="id" scroll={{ x: 800 }}
          pagination={false} size="small" />
      </Card>

      <Modal title="添加供应商" open={addModalVisible} onCancel={() => { setAddModalVisible(false); addForm.resetFields(); }} footer={null}>
        <Form form={addForm} layout="vertical" onFinish={handleAddProvider}>
          <Form.Item name="name" label="标识" rules={[{ required: true, message: '请输入供应商标识' }]} extra="唯一标识，如 deepseek、qwen">
            <Input placeholder="如: custom-api" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="如: 自定义API" />
          </Form.Item>
          <Form.Item name="api_base" label="API地址" rules={[{ required: true, message: '请输入API地址' }]}>
            <Input placeholder="https://api.example.com/v1" />
          </Form.Item>
          <Form.Item name="api_key" label="API密钥" rules={[{ required: true, message: '请输入API密钥' }]}>
            <Input.Password placeholder="sk-xxx" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={addLoading}>确认添加</Button>
        </Form>
      </Modal>

      <Modal title="修改管理密码" open={pwdModalVisible} onCancel={() => { setPwdModalVisible(false); pwdForm.resetFields(); }} footer={null}>
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item name="old" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="当前密码" />
          </Form.Item>
          <Form.Item name="new" label="新密码" rules={[{ required: true, min: 6, message: '新密码至少6位' }]}>
            <Input.Password placeholder="新密码" />
          </Form.Item>
          <Form.Item name="confirm" label="确认新密码" dependencies={['new']}
            rules={[{ required: true, message: '请再次输入新密码' }, ({ getFieldValue }) => ({
              validator(_, value) { return value && getFieldValue('new') === value ? Promise.resolve() : Promise.reject('两次密码不一致'); },
            })]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={pwdLoading}>确认修改</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default OperatorAuth;
